import { FC, KeyboardEventHandler, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ClaimLimitPeriod, Incentive } from 'src/entities/incentives';

import {
	BanOutline,
	Box,
	Button,
	Card,
	CelebratePartyPopperOutline,
	Center,
	ClipboardCheckOutline,
	Display,
	Divider,
	DocumentReportOutline,
	DotsVerticalSolid,
	Flex,
	HStack,
	IconButton,
	InformationCircleOutline,
	Menu,
	MenuButton,
	MenuItem,
	MenuList,
	PencilAltOutline,
	rem,
	Skeleton,
	Spacer,
	Stack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tooltip,
	Tr,
	useDisclosure,
	VStack,
} from '@bonusly/design-system';
import { useBuildRecognizeURL, useQueriesControl } from '@bonusly/url';
import { useAnalytics } from '@bonusly/who-am-i';

import { ReactComponent as IncentiveCircle } from '../../../assets/images/incentives-circle.svg';
import { Reveal } from '../../../components/display/reveal';
import { Paginator } from '../../../components/navigation/paginator';
import { StyledLink } from '../../../components/navigation/styled-link';
import { PageTitle } from '../../../components/page/page-title';
import { SortableHeader } from '../../../components/table/sortable-header';
import { assertPresence } from '../../../lib/runtime';
import { Route } from '../../../modules/vertical-navbar/config/router';
import { useCustomPropertyUserGroups } from '../../../queries/groups';
import { useIncentives, useInvalidateIncentives } from '../../../queries/incentives';
import { Metadata } from '../../../queries/meta';
import { trackClick } from '../../../utils/analytics';
import { INCENTIVES_BLOG_URL, INCENTIVES_HELP_URL } from '../constants';
import { DeactivateIncentiveModal } from './deactivate-incentive-modal';
import { AdminIncentiveTrackingEvent } from './events';

const LoadingSkeleton = () => {
	return (
		<Td>
			<Box py={2}>
				<Skeleton w="full" h={2} />
			</Box>
		</Td>
	);
};

export const IncentivesIndex = () => {
	const { t } = useTranslation('incentives', { keyPrefix: 'admin' });
	const { track } = useAnalytics();
	// The useQueryControl hooks is fine if you're only setting a single query parameter at a time
	// but in this case we potentially need to set multiple query parameters at once
	const [queries, setQueries] = useQueriesControl(['page', 'sort_by', 'sort_direction'], {
		defaultValue: {
			page: '1',
			sort_by: 'name',
			sort_direction: 'ASC',
		},
		allowedValues: {
			sort_by: ['name', 'amount'],
			sort_direction: ['ASC', 'DESC'],
		},
	});

	const parsed = parseInt(queries.page, 10);
	const page = isNaN(parsed) ? 1 : Math.max(1, parsed);

	const { data: [records, meta] = [[], new Metadata()], isLoading } = useIncentives({
		page_number: page,
		sort_by: queries.sort_by,
		sort_direction: queries.sort_direction,
	});

	const { data: [inactiveRecords] = [[]] } = useIncentives({
		state: 'inactive',
	});

	return (
		<>
			<Stack px={10} py={8} spacing={6}>
				<PageTitle>{t('title')}</PageTitle>
				<HStack>
					<Display size="sm">{t('title')}</Display>
					<Spacer />
					<Button as={Link} minW={rem(120)} to="/admin/programs/incentives/new" {...trackClick(AdminIncentiveTrackingEvent.CreateIncentive)}>
						{t('createButtonLabel')}
					</Button>

					{!isLoading && records.length > 0 && (
						<Tooltip label={t('summaryButtonLabel')}>
							<IconButton
								as={Link}
								to={'/admin/incentives/summary'}
								aria-label={t('summaryButtonLabel')}
								backgroundColor="primary.bgAlpha"
								color="blue.600"
								data-testid="incentive-index-report-button"
								{...trackClick(AdminIncentiveTrackingEvent.IncentiveSummary)}
							>
								<DocumentReportOutline />
							</IconButton>
						</Tooltip>
					)}
				</HStack>

				<Card p={0} variant="flat" minW="fit-content" borderRadius="sm">
					<HStack gap={2} p={4} backgroundColor="gray.25" borderRadius="sm">
						<Text fontSize="inherit" maxW={720}>
							{t('indexDescription')}
						</Text>
					</HStack>

					<Table variant="simple">
						<colgroup>
							<col style={{ minWidth: '200px' }} />
							<col />
							<col style={{ minWidth: '200px', maxWidth: '320px' }} />
							<col style={{ minWidth: '120px', maxWidth: '160px' }} />
							<col style={{ minWidth: '96px' }} />
							<col style={{ minWidth: '72px', maxWidth: '96px' }} />
							<col />
						</colgroup>
						<Thead bg="bg.soft">
							<Tr>
								<Th>
									<HStack gap={1} align="center">
										<SortableHeader
											currentDirection={queries.sort_direction}
											currentField={queries.sort_by}
											field="name"
											onSort={(field, direction) => {
												setQueries({ sort_by: field, sort_direction: direction });
												track(AdminIncentiveTrackingEvent.SortIncentives, { field: 'name', direction: direction });
											}}
										>
											{t('nameLabel')}
										</SortableHeader>
									</HStack>
								</Th>
								<Th>
									<HStack gap={1} justify="flex-end" align="center">
										<SortableHeader
											currentDirection={queries.sort_direction}
											currentField={queries.sort_by}
											field="amount"
											onSort={(field, direction) => {
												setQueries({ sort_by: field, sort_direction: direction });
												track(AdminIncentiveTrackingEvent.SortIncentives, { field: 'amount', direction: direction });
											}}
										>
											{t('pointsLabel')}
										</SortableHeader>
									</HStack>
								</Th>
								<Th>
									<SortableHeader currentDirection={'ASC'} currentField={'period'} field="amount" onSort={() => {}}>
										{t('audienceLabel')}
									</SortableHeader>
								</Th>
								<Th>
									<HStack gap={1} justify="flex-start" align="center">
										<Text fontSize="inherit">{t('claimLimitLabel')}</Text>
										<Tooltip label={t('claimLimitTooltip')} hasArrow>
											<Center>
												<InformationCircleOutline size="xs" />
											</Center>
										</Tooltip>
									</HStack>
								</Th>
								<Th>
									<HStack gap={1} justify="flex-start" align="center">
										<Text fontSize="inherit">{t('limitPeriodLabel')}</Text>
										<Tooltip label={t('limitPeriodTooltip')} hasArrow>
											<Center>
												<InformationCircleOutline size="xs" />
											</Center>
										</Tooltip>
									</HStack>
								</Th>
								<Th>
									<HStack gap={1} justify="flex-end" align="center">
										<Text fontSize="inherit">{t('pendingLabel')}</Text>
									</HStack>
								</Th>
								<Th></Th>
							</Tr>
						</Thead>

						<Tbody>
							{records.map((incentive) => (
								<IncentiveRow key={incentive.id} incentive={incentive} />
							))}

							{isLoading &&
								Array.from({ length: 5 }).map((_, index) => (
									<Tr key={index}>
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<LoadingSkeleton />
										<Td></Td>
									</Tr>
								))}

							{!isLoading && records.length === 0 && (
								<Tr>
									<Td colSpan={8}>
										<Center p={8} flexDir="column" textAlign="center">
											<IncentiveCircle />
											<Trans i18nKey="admin.noCelebrations" t={t}>
												<Text as="h4" mt={6}>
													<Text as={Link} color="text.interactive" to={INCENTIVES_BLOG_URL} target="_blank" rel="noopener noreferrer">
														Get ideas on our blog
													</Text>{' '}
													for incentives that could be successful at your company.
												</Text>
											</Trans>
										</Center>
									</Td>
								</Tr>
							)}
						</Tbody>
					</Table>

					{meta.totalPages > 1 && (
						<HStack p={5}>
							<Flex flexBasis={0} flexGrow={1}>
								Viewing {records.length} of {meta.totalCount} items
							</Flex>
							<Paginator
								currentPage={page}
								totalPages={meta.totalPages}
								onSelectPage={(pageNum) => {
									setQueries({ page: pageNum.toString() });
									track(AdminIncentiveTrackingEvent.IndexPaginate, { page: pageNum });
								}}
							/>
							<Flex flexBasis={0} flexGrow={1}></Flex>
						</HStack>
					)}

					{inactiveRecords.length > 0 && (
						<HStack p={5}>
							<Link
								to="/admin/programs/incentives/archived"
								style={{ textDecoration: 'none' }}
								{...trackClick(AdminIncentiveTrackingEvent.ReviewArchivedIncentives)}
							>
								<Text color="text.interactive" _hover={{ textDecoration: 'underline' }}>
									{t('reviewArchivedLink')}
								</Text>
							</Link>
						</HStack>
					)}
				</Card>

				<HStack gap={1} display="flex" align="center">
					<Tooltip>
						<Center>
							<InformationCircleOutline size="xs" color="text.softest" />
						</Center>
					</Tooltip>
					<Text as="h4" fontWeight="medium" fontSize="sm" color="gray.500">
						<Trans i18nKey="admin.learnMoreText" t={t}>
							<Text as={Link} mt={2} fontWeight={500} fontSize={50} color="text.interactive" to={INCENTIVES_HELP_URL} target="_blank" rel="noopener noreferrer">
								Learn more
							</Text>{' '}
							in our Help Center.
						</Trans>
					</Text>
				</HStack>
			</Stack>
		</>
	);
};

type IncentiveRowProps = {
	incentive: Incentive;
};

// I don't like this. We need to constantly shift back and forth between the different attr names
// BOUNTY-54 created to clean this all up
const AttrMap: { [k: string]: string } = {
	department: 'departments',
	team: 'teams',
	location: 'locations',
	'has_reports?': 'roles',
};

export const IncentiveRow: FC<IncentiveRowProps> = ({ incentive }) => {
	const { t } = useTranslation('incentives', { keyPrefix: 'admin' });
	const { track } = useAnalytics();
	const navigate = useNavigate();
	const invalidateIncentives = useInvalidateIncentives();
	const deactivateModal = useDisclosure();
	const { data } = useCustomPropertyUserGroups();

	assertPresence(data);

	const recognizeURL = useBuildRecognizeURL();
	const pendingAwardsURL = recognizeURL(Route.AdminAwardsPending);
	const detailPath = `/admin/programs/incentives/${incentive.id}/edit`;
	const reviewPath = recognizeURL(`/company/users/${incentive.giverId}`);

	const handleViewIncentive = () => {
		navigate(detailPath);
	};

	const handleKeyDown: KeyboardEventHandler<HTMLTableRowElement> = (e) => {
		if (e.key === 'Enter') {
			handleViewIncentive();
		}
	};

	const audienceDisplay = useMemo(() => {
		if (!incentive.conditions || incentive.conditions.length == 0) {
			return t('everyone');
		}

		return incentive.conditions.map((condition) => {
			// Types need to be updated at a more global level to avoid (any)s here
			const key = AttrMap[condition.attr] ?? condition.attr;
			const options = (data as any)[key];

			if (!options) {
				return condition.value;
			}

			const option = options.find((o: any) => o.key === condition.value);

			return option ? option.name : condition.value;
		});
	}, [incentive, data]);

	const getClaimLimitDisplay = (incentive: Incentive): string => {
		if (incentive.claimLimit !== null) {
			return t('claimLimitPerUser', { count: incentive.claimLimit });
		} else if (incentive.quantity !== null) {
			return t('claimLimitTotal', { count: incentive.quantity });
		} else {
			return t('none');
		}
	};

	const getClaimLimitPeriodDisplay = (periodType: ClaimLimitPeriod | null): string => {
		if (periodType === null) {
			return '-';
		}

		return t(`limitPeriod.${periodType}`);
	};

	return (
		<>
			<Tr
				cursor="pointer"
				data-testid={`incentive-${incentive.id}`}
				key={incentive.id}
				onClick={() => {
					navigate(detailPath);
					track(AdminIncentiveTrackingEvent.SelectIncentive, { incentiveId: incentive.id });
				}}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				transitionProperty="background-color"
				transitionDuration="fast"
				_hover={{
					bg: 'secondary.bgAlpha',
				}}
			>
				<Td fontWeight="semibold">{incentive.name}</Td>
				<Td textAlign="end">{incentive.amount}</Td>
				<Td>{audienceDisplay}</Td>
				<Td>{getClaimLimitDisplay(incentive)}</Td>
				<Td>{getClaimLimitPeriodDisplay(incentive.periodType)}</Td>
				<Td textAlign="end" color="blue.500">
					{incentive.pendingApprovals > 0 ? (
						<StyledLink p={2} _hover={{ textDecoration: 'underline' }} to={recognizeURL('/admin/awards/pending')} onClick={(e) => e.stopPropagation()}>
							{incentive.pendingApprovals}
						</StyledLink>
					) : (
						''
					)}
				</Td>
				<Td>
					<Menu>
						<MenuButton as={IconButton} aria-label={'Menu Label'} onClick={(e) => e.stopPropagation()} variant="ghost">
							<DotsVerticalSolid size="sm" />
						</MenuButton>
						<MenuList width={240}>
							<Reveal if={incentive.pendingApprovals > 0}>
								<MenuItem
									as={Link}
									to={pendingAwardsURL}
									onClick={(e) => {
										track(AdminIncentiveTrackingEvent.ApproveIncentive, { incentiveId: incentive.id });
										e.stopPropagation();
									}}
									py={3}
								>
									<HStack align="start" gap={1}>
										<ClipboardCheckOutline />
										<VStack align="start" gap={0}>
											<Text>{t('approveLabel')}</Text>
										</VStack>
									</HStack>
								</MenuItem>
							</Reveal>
							<MenuItem
								as={Link}
								to={reviewPath}
								onClick={(e) => {
									track(AdminIncentiveTrackingEvent.ReviewPosts, { incentiveId: incentive.id });
									e.stopPropagation();
								}}
								py={3}
							>
								<HStack align="start" gap={1}>
									<CelebratePartyPopperOutline />
									<VStack align="start" gap={0}>
										<Text>{t('reviewLabel')}</Text>
									</VStack>
								</HStack>
							</MenuItem>
							<Divider borderColor="grayAlpha.300" borderWidth="0.5px" my={rem(1)} />
							<MenuItem
								as={Link}
								to={detailPath}
								onClick={(e) => {
									track(AdminIncentiveTrackingEvent.EditIncentive, { incentiveId: incentive.id });
									e.stopPropagation();
								}}
								py={3}
							>
								<HStack align="start" gap={1}>
									<PencilAltOutline />
									<VStack align="start" gap={0}>
										<Text>{t('editLabel')}</Text>
									</VStack>
								</HStack>
							</MenuItem>
							<MenuItem
								as={Link}
								onClick={(e) => {
									e.stopPropagation();
									deactivateModal.onOpen();
								}}
								py={3}
							>
								<HStack align="start" gap={1}>
									<BanOutline />
									<VStack align="start" gap={0}>
										<Text>{t('deactivateLabel')}</Text>
									</VStack>
								</HStack>
							</MenuItem>
						</MenuList>
					</Menu>
				</Td>
			</Tr>

			<DeactivateIncentiveModal
				incentive={incentive}
				isOpen={deactivateModal.isOpen}
				onClose={deactivateModal.onClose}
				onSuccess={() => invalidateIncentives()}
				redirectPath=""
			/>
		</>
	);
};
